import { useCallback, useEffect, useRef, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { Socket } from "socket.io-client";
import type { Msg } from "../../../types/chat";
import { loadReadReceiptsSetting } from "../chatUtils";

const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

/**
 * Read tracking for the open channel: marks it read (advancing the server watermark) when the
 * user is looking at the bottom of it, keeps the read-receipt / invisible-status refs current,
 * and tracks which channels are locally "read but not yet confirmed" so a stale /unread
 * hydration can't resurface them.
 */
export function useChannelReadState({
  socket,
  msgs,
  msgsByKeyRef,
  listRef,
  currentVoidId,
  currentChannelId,
  members,
  userId,
  markUnread,
  setNotified,
  setUnread,
}: {
  socket: Socket;
  msgs: Msg[];
  msgsByKeyRef: RefObject<Record<string, Msg[]>>;
  listRef: RefObject<HTMLDivElement | null>;
  currentVoidId: string;
  currentChannelId: string;
  members: { id: string; status?: string }[];
  userId?: string;
  markUnread: (fid: string) => void;
  setNotified: Dispatch<SetStateAction<Record<string, boolean>>>;
  setUnread: Dispatch<SetStateAction<Record<string, number>>>;
}) {
  // Channels this device has marked read this session. The server advances the
  // read watermark asynchronously over the socket, but the /unread hydration is a
  // separate HTTP read that can race ahead of it (and the socket re-hydrates on
  // every navigation), so a hydration could resurface a channel we just read with
  // a stale count. We treat these as read until a genuinely new message arrives
  // (markUnreadTracked clears the entry) or the server confirms the read.
  const pendingReadRef = useRef<Set<string>>(new Set());
  // Last (channel -> messageId) we actually emitted read:up_to for. The auto-read
  // effect re-runs on every scroll/visibility/render tick, so without this guard
  // opening a channel fired hundreds of identical read:up_to events (each one a
  // full server round-trip: watermark write + receipt + broadcasts + pushes).
  const lastReadEmitRef = useRef<Record<string, string>>({});
  const markChannelRead = useCallback((voidId: string, chanId: string, lastMessageId?: string) => {
    if (!voidId || !chanId) return;
    const fid = fq(voidId, chanId);
    // When the caller has no message id (e.g. opening a channel), fall back to the
    // last message we've actually loaded. We must advance the server watermark to
    // truly clear unread — a local-only clear would resurface on the next unread
    // hydration. With no loaded messages there's nothing seen to mark, so bail.
    const loaded = msgsByKeyRef.current?.[fid];
    const readUpToId = lastMessageId || loaded?.[loaded.length - 1]?.id;
    if (!readUpToId) return;
    // Skip if we've already reported this exact read position for this channel.
    // A genuinely newer message changes readUpToId and re-opens the emit.
    if (lastReadEmitRef.current[fid] === readUpToId) return;
    lastReadEmitRef.current[fid] = readUpToId;
    pendingReadRef.current.add(fid);
    // Always advance the server read watermark so unread stays correct across
    // devices. The read-receipts / invisible setting only controls whether the
    // public "seen" receipt is broadcast to others (silent) — it must not
    // suppress our own read tracking, or the badge would resurface on the next
    // server unread hydration.
    const silent = !readReceiptsEnabledRef.current || selfInvisibleRef.current;
    socket.emit('read:up_to', { channelId: fid, lastMessageId: readUpToId, silent });
    setNotified((prev) => {
      if (!prev[fid]) return prev;
      const next = { ...prev };
      delete next[fid];
      return next;
    });
    setUnread((prev) => {
      if (!prev[fid]) return prev;
      const next = { ...prev };
      delete next[fid];
      return next;
    });
  }, [setNotified, setUnread, socket, msgsByKeyRef]);
  // Bump unread for a channel AND drop it from the read-pending set: a genuinely
  // new message means the channel is unread again, so the next /unread hydration
  // should once more be allowed to reflect the server's count for it.
  const markUnreadTracked = useCallback((fid: string) => {
    pendingReadRef.current.delete(fid);
    markUnread(fid);
  }, [markUnread]);
  // Auto-mark current channel read when user is focused/at-bottom and clear throttles
  useEffect(() => {
    const maybeMarkRead = () => {
      if (!currentVoidId || !currentChannelId) return;
      // Visibility is the reliable "user is looking at this channel" signal. We do
      // NOT additionally require document.hasFocus(): in the Android WebView (and
      // some mobile browsers) hasFocus() returns false even when the page is
      // visible and frontmost, which blocked the auto-read entirely — so the read
      // watermark never advanced server-side and the space's unread dot would
      // reappear the moment the user left the channel (the next /unread hydration
      // re-surfaced a "read" channel because the server never learned it was read).
      if (document.visibilityState !== 'visible') return;
      const list = msgs;
      const lastId = list[list.length - 1]?.id;
      if (!lastId) return;
      const el = listRef.current;
      if (el) {
        const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 40;
        if (!nearBottom) return;
      }
      markChannelRead(currentVoidId, currentChannelId, lastId);
    };
    maybeMarkRead();
    const onVis = () => maybeMarkRead();
    const onFocus = () => maybeMarkRead();
    const onScroll = () => maybeMarkRead();
    const el = listRef.current;
    if (el) el.addEventListener('scroll', onScroll);
    window.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      if (el) el.removeEventListener('scroll', onScroll);
    };
  }, [msgs, currentVoidId, currentChannelId, markChannelRead, listRef]);
  const readReceiptsEnabledRef = useRef<boolean>(loadReadReceiptsSetting());
  const selfInvisibleRef = useRef<boolean>(false);
  useEffect(() => {
    const sync = () => { readReceiptsEnabledRef.current = loadReadReceiptsSetting(); };
    sync();
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === "settings") sync();
    };
    const onPrivacyEvent = (e: Event) => {
      const detail = (e as CustomEvent)?.detail as any;
      if (detail && typeof detail.readReceipts === "boolean") {
        readReceiptsEnabledRef.current = !!detail.readReceipts;
      } else {
        sync();
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("echo:privacy-changed", onPrivacyEvent as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("echo:privacy-changed", onPrivacyEvent as EventListener);
    };
  }, []);
  useEffect(() => {
    const meMem = members.find(m => m.id === userId);
    const status = String(meMem?.status || '').toLowerCase();
    selfInvisibleRef.current = status === 'invisible';
  }, [members, userId]);
  return { pendingReadRef, markChannelRead, markUnreadTracked, readReceiptsEnabledRef, selfInvisibleRef };
}
